/**
 * Shared exports for client-side usage
 * This file only exports browser-safe types and enums
 *
 * IMPORTANT: Do not import from service files or Prisma client here!
 * Only import from browser-safe sources like ./generated/prisma/enums
 */

// Re-export browser-safe enums from Prisma
export {
  DocumentCategory,
  DocumentSubType,
  DutyStatus,
  DutyPeriodType,
  PassengerRole,
  PassengerTier,
  TemperatureUnit,
  TimezoneDisplay,
  CrewRole
} from './generated/prisma/enums'

// Import CrewRole for local use
import { CrewRole } from './generated/prisma/enums'

// Crew role labels for display (maps enum values to human-readable labels)
export const crewRoleLabels: Record<CrewRole, string> = {
  [CrewRole.FlightAttendant]: 'Flight Attendant',
  [CrewRole.JuniorFlightAttendant]: 'Junior Flight Attendant',
  [CrewRole.SeniorFlightAttendant]: 'Senior Flight Attendant',
  [CrewRole.LeadFlightAttendant]: 'Lead Flight Attendant',
  [CrewRole.JuniorPilot]: 'Junior Pilot',
  [CrewRole.SeniorPilot]: 'Senior Pilot',
  [CrewRole.LeadPilot]: 'Lead Pilot',
  [CrewRole.Engineer]: 'Engineer',
  [CrewRole.GeneralManager]: 'General Manager',
}

// Crew roles array for iteration (derived from generated enum)
export const crewRoles = Object.values(CrewRole)

// Pilot roles — used to gate flight-log access (client sections + withPilotAuth)
export const pilotCrewRoles = [
  CrewRole.JuniorPilot,
  CrewRole.SeniorPilot,
  CrewRole.LeadPilot,
] as const

export function isPilotRole(role: CrewRole | string | null | undefined): boolean {
  return !!role && (pilotCrewRoles as readonly string[]).includes(role)
}

export const flightAttendantCrewRoles = [
  CrewRole.FlightAttendant,
  CrewRole.JuniorFlightAttendant,
  CrewRole.SeniorFlightAttendant,
  CrewRole.LeadFlightAttendant,
] as const

export function isFlightAttendantRole(role: CrewRole | string | null | undefined): boolean {
  return !!role && (flightAttendantCrewRoles as readonly string[]).includes(role)
}

// Compact role marker for avatars (roster Plan view): P pilot, FA flight
// attendant, E engineer. General managers / no role render nothing.
export type CrewRoleBadge = 'P' | 'FA' | 'E'
export function crewRoleBadge(role: CrewRole | string | null | undefined): CrewRoleBadge | null {
  if (isPilotRole(role)) return 'P'
  if (isFlightAttendantRole(role)) return 'FA'
  if (role === CrewRole.Engineer) return 'E'
  return null
}

// Import for local use
import { DocumentSubType } from './generated/prisma/enums'

// Document sub-type labels (maps enum values to display labels)
export const documentSubTypeLabels: Record<DocumentSubType, string> = {
  Passport: 'Passport',
  Visa: 'Visa',
  TrainingDocument: 'Training Document',
  CrewID: 'Crew ID',
  MedicalCertificate: 'Medical Certificate',
  License: 'License',
  TravelID: 'Travel ID',
  Manual: 'Manual',
  SOP: 'SOP',
  Checklist: 'Checklist',
  Policy: 'Policy',
  Other: 'Other',
}

// Crew document sub-types (subset for crew documents)
export const crewDocumentSubTypes = [
  DocumentSubType.Passport,
  DocumentSubType.Visa,
  DocumentSubType.TrainingDocument,
  DocumentSubType.CrewID,
  DocumentSubType.MedicalCertificate,
  DocumentSubType.License,
  DocumentSubType.Other,
] as const

// Passenger document sub-types (subset for passenger documents)
export const passengerDocumentSubTypes = [
  DocumentSubType.Passport,
  DocumentSubType.Visa,
  DocumentSubType.TravelID,
  DocumentSubType.Other,
] as const

// Compliance document sub-types (subset for compliance documents)
export const complianceDocumentSubTypes = [
  DocumentSubType.Manual,
  DocumentSubType.SOP,
  DocumentSubType.Checklist,
  DocumentSubType.Policy,
  DocumentSubType.Other,
] as const

// Aircraft document sub-types (subset for per-aircraft operational documents)
export const aircraftDocumentSubTypes = [
  DocumentSubType.Manual,
  DocumentSubType.SOP,
  DocumentSubType.Checklist,
  DocumentSubType.Policy,
  DocumentSubType.License,
  DocumentSubType.Other,
] as const

// Import DutyPeriodType for local use
import { DutyPeriodType } from './generated/prisma/enums'

// Duty period type labels (maps enum values to display labels)
export const dutyPeriodTypeLabels: Record<DutyPeriodType, string> = {
  SevenDaysOffPerMonth: '7 Days off per month',
  TwoWeeksOnOff: '2 Weeks on/off',
  ThreeWeeksOnOff: '3 Weeks on/off',
  OneMonthOnOff: '1 Month on/off',
  TwoMonthsOnOff: '2 Months on/off',
  Custom: 'Custom',
}

// Duty period options for selection
export const dutyPeriodOptions: DutyPeriodType[] = [
  DutyPeriodType.SevenDaysOffPerMonth,
  DutyPeriodType.TwoWeeksOnOff,
  DutyPeriodType.ThreeWeeksOnOff,
  DutyPeriodType.OneMonthOnOff,
  DutyPeriodType.TwoMonthsOnOff,
  DutyPeriodType.Custom,
]
